import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Hook especializado para la arquitectura SaaS del sistema
 * 
 * JERARQUÍA DE ROLES:
 * 1. Super Admin (Sistema): Control total cross-company
 * 2. Admin (Empresa): Control total de su empresa
 * 3. Manager/User: Permisos limitados según rol
 */
export function useSystemRoles() {
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();
  const { hasRole, hasPermission, loading: permissionsLoading } = usePermissions();

  const loading = superAdminLoading || permissionsLoading;

  // Super Admin = Dueño del sistema (bypass total)
  const isSystemOwner = isSuperAdmin;

  // Admin de Empresa = Admin de su empresa específica
  const isCompanyAdmin = isSuperAdmin || hasRole('admin');

  // Manager = Nivel intermedio con algunos permisos de gestión
  const isManager = isSuperAdmin || hasRole('admin') || hasRole('manager');

  // Usuario regular = Solo permisos básicos
  const isRegularUser = !isSuperAdmin && !hasRole('admin') && !hasRole('manager');

  // Funciones de verificación combinadas
  const canManageSystem = () => isSuperAdmin;
  
  const canManageCompany = () => isSuperAdmin || hasRole('admin');
  
  const canManageUsers = () => isSuperAdmin || hasPermission('manage_users');
  
  const canManageProducts = () => isSuperAdmin || hasPermission('manage_products');
  
  const canManageSales = () => isSuperAdmin || hasPermission('manage_sales');
  
  const canViewReports = () => isSuperAdmin || hasPermission('view_reports');
  
  const canManageInventory = () => isSuperAdmin || hasPermission('manage_inventory');

  // Función para verificar permisos con jerarquía
  const hasPermissionOrHigher = (permission: string): boolean => {
    return isSuperAdmin || hasPermission(permission);
  };

  // Función para verificar roles con jerarquía
  const hasRoleOrHigher = (role: string): boolean => {
    if (isSuperAdmin) return true;
    if (role === 'user') return true; // Todos pueden ser usuarios
    if (role === 'manager' && hasRole('admin')) return true;
    return hasRole(role);
  };

  return {
    // Estados básicos
    loading,
    isSuperAdmin,
    isSystemOwner,
    isCompanyAdmin,
    isManager,
    isRegularUser,
    
    // Funciones de verificación
    canManageSystem,
    canManageCompany,
    canManageUsers,
    canManageProducts,
    canManageSales,
    canViewReports,
    canManageInventory,
    hasPermissionOrHigher,
    hasRoleOrHigher,
    
    // Acceso a hooks base
    hasRole,
    hasPermission
  };
}